import { CommonActivityType } from './activity-history/common-activity-type.enum';

/**
 * Activity types tracked in the license_activity table.
 * Extends CommonActivityType with License-specific types.
 */
export const LicenseActivityType = {
	...CommonActivityType,
	// License-specific types
	DISCIPLINARY_ACTION_CREATED: 'disciplinary_action_created',
	DISCIPLINARY_ACTION_UPDATED: 'disciplinary_action_updated',
	DISCIPLINARY_ACTION_DELETED: 'disciplinary_action_deleted',
	// Match-related types
	MATCH_CONFIRMED: 'match_confirmed',
	MATCH_REJECTED: 'match_rejected',
	MATCH_REVERTED: 'match_reverted',
	MATCH_MANUAL: 'match_manual',
	// Registry sync types
	REGISTRY_STATUS_CHANGED: 'registry_status_changed',
	// Link drift (2026-09-05): the regulator stopped linking this licence to a
	// licence the client also holds (e.g. the vet left the premise).
	LINK_ENDED: 'link_ended',
	// Compliance review (2026-09-20): a compliance officer recorded a
	// disposition + mandatory note against a compliance-list item (today, the
	// Adverse Action list — a board disciplinary action on a license that is
	// still in force). The disposition, the note and the reviewed board
	// snapshot ride in `metadata`; the row IS the audit trail for that
	// decision. Written by LicenseComplianceReviewService.
	COMPLIANCE_REVIEW_RECORDED: 'compliance_review_recorded',
	// Compliance review re-opened (PD-16615): the officer undid the current
	// decision, so the item is back on its list. The undone review's id and
	// disposition ride in `metadata`. Written by LicenseComplianceReviewService.
	COMPLIANCE_REVIEW_REOPENED: 'compliance_review_reopened',
	// Auto-sunset (2026-09-20): every board action on this license's registry
	// record carried an end date, and all of them are now strictly past, so the
	// item left the Adverse Action queue without anyone reviewing it. Until this
	// type existed the sunset was computed on read and left no trace, so "which
	// item closed itself, and when" was unanswerable and the What Workd Did tile
	// had to derive its figure from end dates instead of from events. Written by
	// BoardActionSunsetService, once per distinct closure — see its `sunsetKey`.
	BOARD_ACTION_SUNSET: 'board_action_sunset',
} as const;

export type LicenseActivityType =
	(typeof LicenseActivityType)[keyof typeof LicenseActivityType];

export type LicenseActivityTypeValue = LicenseActivityType;

/**
 * Labels for activity types (for display in filter bar)
 */
export const LICENSE_ACTIVITY_TYPE_LABELS: Record<LicenseActivityType, string> =
	{
		// Common types
		[LicenseActivityType.CREATED]: 'Created',
		[LicenseActivityType.UPDATED]: 'Updated',
		[LicenseActivityType.DELETED]: 'Deleted',
		[LicenseActivityType.RECOVERED]: 'Recovered',
		[LicenseActivityType.STATUS_CHANGED]: 'Status Changed',
		[LicenseActivityType.PRIORITY_CHANGED]: 'Priority Changed',
		[LicenseActivityType.ASSIGNEE_CHANGED]: 'Assignee Changed',
		[LicenseActivityType.NOTE_ADDED]: 'Note Added',
		[LicenseActivityType.NOTE_UPDATED]: 'Note Updated',
		[LicenseActivityType.NOTE_DELETED]: 'Note Deleted',
		[LicenseActivityType.TASK_LINKED]: 'Task Linked',
		[LicenseActivityType.TASK_UNLINKED]: 'Task Unlinked',
		[LicenseActivityType.TASK_CREATED]: 'Task Created',
		[LicenseActivityType.TASK_COMPLETED]: 'Task Completed',
		[LicenseActivityType.TASK_UPDATED]: 'Task Updated',
		[LicenseActivityType.TASK_DELETED]: 'Task Deleted',
		[LicenseActivityType.FILE_ATTACHED]: 'File Attached',
		[LicenseActivityType.FILE_REMOVED]: 'File Removed',
		[LicenseActivityType.ENTITY_LINKED]: 'Entity Linked',
		[LicenseActivityType.ENTITY_UNLINKED]: 'Entity Unlinked',
		[LicenseActivityType.WATCHER_ADDED]: 'Watcher Added',
		[LicenseActivityType.WATCHER_REMOVED]: 'Watcher Removed',
		[LicenseActivityType.AI_SESSION]: 'AI Session',
		// License-specific types
		[LicenseActivityType.DISCIPLINARY_ACTION_CREATED]:
			'Disciplinary Action Created',
		[LicenseActivityType.DISCIPLINARY_ACTION_UPDATED]:
			'Disciplinary Action Updated',
		[LicenseActivityType.DISCIPLINARY_ACTION_DELETED]:
			'Disciplinary Action Deleted',
		// Match-related labels
		[LicenseActivityType.MATCH_CONFIRMED]: 'Match Confirmed',
		[LicenseActivityType.MATCH_REJECTED]: 'Match Rejected',
		[LicenseActivityType.MATCH_REVERTED]: 'Match Reverted',
		[LicenseActivityType.MATCH_MANUAL]: 'Manual Match',
		[LicenseActivityType.REGISTRY_STATUS_CHANGED]: 'Registry Status Changed',
		[LicenseActivityType.LINK_ENDED]: 'Regulator Link Ended',
		[LicenseActivityType.COMPLIANCE_REVIEW_RECORDED]:
			'Compliance Review Recorded',
		[LicenseActivityType.COMPLIANCE_REVIEW_REOPENED]:
			'Compliance Review Re-opened',
		[LicenseActivityType.BOARD_ACTION_SUNSET]: 'Board Action Ended',
	};

/**
 * Group activity types for filter bar categories
 */
export const LICENSE_ACTIVITY_GROUPS = {
	all: [] as Array<LicenseActivityType>,
	status: [
		LicenseActivityType.CREATED,
		LicenseActivityType.UPDATED,
		LicenseActivityType.STATUS_CHANGED,
		LicenseActivityType.ASSIGNEE_CHANGED,
	],
	disciplinary: [
		LicenseActivityType.DISCIPLINARY_ACTION_CREATED,
		LicenseActivityType.DISCIPLINARY_ACTION_UPDATED,
		LicenseActivityType.DISCIPLINARY_ACTION_DELETED,
		// A compliance review is a decision ABOUT a board action, so it reads
		// in the same filter bucket rather than in a new group of its own.
		LicenseActivityType.COMPLIANCE_REVIEW_RECORDED,
		LicenseActivityType.COMPLIANCE_REVIEW_REOPENED,
		// Same bucket, same reason: the board action ending is the last chapter
		// of the disciplinary story the other four types tell.
		LicenseActivityType.BOARD_ACTION_SUNSET,
	],
	notes: [
		LicenseActivityType.NOTE_ADDED,
		LicenseActivityType.NOTE_UPDATED,
		LicenseActivityType.NOTE_DELETED,
	],
	tasks: [
		LicenseActivityType.TASK_LINKED,
		LicenseActivityType.TASK_UNLINKED,
		LicenseActivityType.TASK_CREATED,
		LicenseActivityType.TASK_COMPLETED,
	],
	files: [LicenseActivityType.FILE_ATTACHED, LicenseActivityType.FILE_REMOVED],
	watchers: [
		LicenseActivityType.WATCHER_ADDED,
		LicenseActivityType.WATCHER_REMOVED,
	],
	matching: [
		LicenseActivityType.MATCH_CONFIRMED,
		LicenseActivityType.MATCH_REJECTED,
		LicenseActivityType.MATCH_REVERTED,
		LicenseActivityType.MATCH_MANUAL,
		LicenseActivityType.REGISTRY_STATUS_CHANGED,
		LicenseActivityType.LINK_ENDED,
	],
};
